import { useSelector } from 'react-redux';
import { FlexContainer, Spinner, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { userStageSelector } from 'Store/modules/validations/selectors';
import useTranslation from 'Hooks/useTranslation';
import {
  useFetchPreviousYearIsRequest,
  useFetchSatUserCacheInfo,
} from 'Containers/Dashboard/hooks';
import { PreviousYearStatus } from 'Containers/PreviousYears/types';
import CustomTransition from 'Components/CustomTransition';
import IsCalculatingBanner from 'Components/IsCalculatingBanner';
import ZeroBanner from 'Components/ZeroBanner';
import PreviousYearContent from 'Components/PreviousYearContent';
import InitialAABanner from 'Components/InitialAABaner';
import ButtonsBanner from 'Components/ButtonsBanner';
import * as Styled from './styles';
import {
  getModeByPreviousYearStatus,
  isPreDaUserOrIsCalculating,
  isTaxYear2019Recoverable,
  needEfirma,
  renderCalculatingInfo,
  shouldRenderPreviousYearContent,
  shouldRenderZeroBanner,
} from './utils';
import { PreviousYearBannerProps } from './types';

const PreviousYearBanner = ({
  data,
  isUserWithPreFiling,
}: PreviousYearBannerProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.PY);
  const satUserData = useFetchSatUserCacheInfo();
  const { data: isRequest, isLoading: loading } = useFetchPreviousYearIsRequest();
  const userStage = useSelector(userStageSelector);
  const isAnalyze = isPreDaUserOrIsCalculating(userStage?.toString(), data);
  const isSubmitted = data?.stage === PreviousYearStatus.SUBMITTED;

  const renderCalculatingBanner = renderCalculatingInfo(isAnalyze, isRequest?.isRequested, data);
  const renderZeroBanner = shouldRenderZeroBanner(
    userStage?.toString(),
    isRequest?.isRequested,
    data,
    isSubmitted,
    isUserWithPreFiling
  );
  const renderPreviousYearContent = shouldRenderPreviousYearContent(
    userStage?.toString(),
    isRequest?.isRequested,
    data,
    isSubmitted,
    isUserWithPreFiling
  );

  const mode = getModeByPreviousYearStatus(
    renderCalculatingBanner,
    renderZeroBanner,
    renderPreviousYearContent,
    needEfirma(satUserData, isRequest?.isRequested, data),
    isTaxYear2019Recoverable(data)
  );

  const isCompensation = data?.compensatedTaxReturns;

  return (
    <FlexContainer direction="column" gap="dimension_24">
      <Styled.TitleContainer direction="column" gap="dimension_08">
        <Text type="headline" size="S_L_S" color="dark">
          {t('PreviousYears.Dashboard.Title')}
        </Text>
        <Text type="paragraphs" size="S_S_S" color="dark">
          {t('PreviousYears.Dashboard.Subtitle')}
        </Text>
      </Styled.TitleContainer>
      <Styled.Container size="M" mode={isCompensation ? 'cold_gradient' : mode} hasBorder={false}>
        {loading ? (
          <Styled.SpinnerContainer justify="center" align="center">
            <Spinner size="M" align="center" justify="center" />
          </Styled.SpinnerContainer>
        ) : (
          <CustomTransition start>
            {renderCalculatingBanner && !isCompensation && <IsCalculatingBanner />}
            {((isCompensation && data) || (renderZeroBanner && data)) && (
              <ZeroBanner data={data} isRequest={isRequest} />
            )}
            {renderPreviousYearContent && data && !isCompensation && (
              <FlexContainer
                align="flex-start"
                direction={data ? 'column' : 'row'}
                justify={data ? 'flex-start' : 'space-between'}
                gap="dimension_08"
              >
                <PreviousYearContent
                  data={data}
                  needEfirma={needEfirma(satUserData, isRequest?.isRequested, data)}
                />
                <ButtonsBanner data={data} satUserData={satUserData} isRequest={isRequest} />
              </FlexContainer>
            )}
            {!isRequest?.isRequested && !isCompensation && (
              <InitialAABanner data={data} isRequest={isRequest} />
            )}
          </CustomTransition>
        )}
      </Styled.Container>
    </FlexContainer>
  );
};

export default PreviousYearBanner;
